import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { EsignatureDocument } from "../../../../api/graphql";
import { EsignatureDocumentListStore } from "../../store";
import { esignatureRecipientDisplayId } from "../../../esignatureRecipient/store/EsignatureRecipientCommonStore";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: EsignatureDocumentListStore };

export const EsignatureDocumentListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<EsignatureDocument>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: EsignatureDocument, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<EsignatureDocument>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "provider") {
          return {
            ...col,
            id: "provider",
            accessorFn: (row: EsignatureDocument) => {
              const v = (row as any).provider;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EsignatureProviderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "status") {
          return {
            ...col,
            id: "status",
            accessorFn: (row: EsignatureDocument) => {
              const v = (row as any).status;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.EsignatureDocumentStatusOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "sentAt") {
          return {
            ...col,
            id: "sentAt",
            accessorFn: (row: EsignatureDocument) =>
              row.sentAt
                ? new Date(row.sentAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "completedAt") {
          return {
            ...col,
            id: "completedAt",
            accessorFn: (row: EsignatureDocument) =>
              row.completedAt
                ? new Date(row.completedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "recipients") {
          return {
            ...col,
            id: "recipients",
            accessorFn: (row: EsignatureDocument) =>
              row.recipients?.length
                ? row.recipients
                    .map((x: any) => esignatureRecipientDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.esignatureDocumentCurrentPage || [];

    return (
      <div id={"ListOfEsignatureDocument"}>
        <div id={"StartOfListingOfEsignatureDocument"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
